import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import useDebouncedSearch from "hooks/useDebouncedSearch";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";
import { TablePage } from "@outpave/ds";
import CompanyFilterType from "../../enums/CompanyFilterType";
import AccountsTable from "../../components/platform/accounts/AccountsTable";
import { AccountDetailsDrawerType } from "enums/AccountDetailsDrawerType";
import EditAccountDrawer from "../../components/platform/accounts/EditAccountDrawer";
import AccountDetailsDrawer from "components/platform/accounts/AccountDetailsDrawer";
import useAccountData from "hooks/useAccountData";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";

export type CompanyFilterCriteriaProps = {
  companyFilterType?: string;
  searchTerm?: string;
};

/** What a link into this page can hand it, such as a dashboard row naming its company. */
export interface AccountsLocationState {
  search?: string;
}

const Accounts: React.FC = () => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const initialSearch = (state as AccountsLocationState | null)?.search ?? "";
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const search = useDebouncedSearch(setSearchTerm, initialSearch);

  const [filterCriteria, setFilterCriteria] =
    useState<CompanyFilterCriteriaProps>({
      companyFilterType: CompanyFilterType.ShowAll,
      searchTerm: initialSearch,
    });

  const tabConfigurations = [
    {
      label: t("Show All"),
      companyFilterType: CompanyFilterType.ShowAll,
    },
    {
      label: t("Active"),
      companyFilterType: CompanyFilterType.Active,
    },
  ];

  useEffect(() => {
    setFilterCriteria({
      companyFilterType: tabConfigurations[activeTab].companyFilterType,
      searchTerm: searchTerm,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchTerm]);

  const [refreshAccounts, setRefreshAccounts] = useState(0);

  const handleRefreshAccounts = () => {
    setRefreshAccounts((prev) => prev + 1);
  };

  const [drawerState, setDrawerState] = useState<{
    open: boolean;
    id: string;
    drawerType: AccountDetailsDrawerType | null;
  }>({ open: false, id: "", drawerType: null });

  const handleOpenEditAccountDrawer = () => {
    setDrawerState({
      open: true,
      id: "",
      drawerType: AccountDetailsDrawerType.NewAccount,
    });
  };

  const handleOpenViewDetailsDrawer = (accountId: string) => {
    setDrawerState({
      open: true,
      id: accountId,
      drawerType: AccountDetailsDrawerType.EditExistingAccount,
    });
  };

  const handleCloseDrawers = (
    refresh: boolean = false,
    openContactDetailsWithId?: string,
    newContactBusinessName?: string,
  ) => {
    refresh && handleRefreshAccounts();

    // only open the drawer if reactivating an inative contact
    if (openContactDetailsWithId && newContactBusinessName) {
      setDrawerState({
        open: true,
        id: openContactDetailsWithId,
        drawerType: AccountDetailsDrawerType.EditExistingAccount,
      });
    } else {
      setDrawerState({
        open: false,
        id: "",
        drawerType: null,
      });
    }
  };

  return (
    <TablePage
      title={t("platform.manageAccounts")}
      primaryAction={
        <Button onClick={handleOpenEditAccountDrawer}>
          {t("platform.addAccount")}
        </Button>
      }
      tabs={{
        items: tabConfigurations.map((tab, index) => ({
          id: String(index),
          label: t(tab.label),
        })),
        activeId: String(activeTab),
        onChange: (id) => setActiveTab(Number(id)),
      }}
      search={{ ...search, placeholder: t("Search accounts") }}
    >
      <AccountsTable
        handleOpenViewDetailsDrawer={handleOpenViewDetailsDrawer}
        filterCriteria={filterCriteria}
        refreshAccounts={refreshAccounts}
      />

      {drawerState.drawerType && (
        <UnifiedDrawerContainer
          accountId={drawerState.id}
          open={drawerState.open}
          handleClose={handleCloseDrawers}
          refreshAccounts={() => {}}
          drawerType={drawerState.drawerType}
        />
      )}
    </TablePage>
  );
};

interface UnifiedDrawerContainerProps {
  accountId?: string;
  open: boolean;
  handleClose: (refresh?: boolean, openAccountDetailsWithId?: string) => void;
  drawerType: AccountDetailsDrawerType;
  refreshAccounts: () => void;
}

const UnifiedDrawerContainer: React.FC<UnifiedDrawerContainerProps> = ({
  accountId,
  open,
  handleClose,
  drawerType,
  refreshAccounts,
}) => {
  const { formikAddAccount, isLoading, isSavingAccount } = useAccountData({
    handleClose: handleClose,
    accountId: accountId || "",
    refreshAccounts: refreshAccounts,
  });
  const isEditExistingAccount =
    drawerType === AccountDetailsDrawerType.EditExistingAccount;
  return (
    <>
      {isEditExistingAccount ? (
        <AccountDetailsDrawer
          formik={formikAddAccount}
          open={open}
          handleCloseDrawer={handleClose}
          drawerType={AccountDetailsDrawerType.EditExistingAccount}
          accountId={accountId}
          savingAccount={isSavingAccount}
        />
      ) : (
        <EditAccountDrawer
          formik={formikAddAccount}
          open={open}
          handleClose={handleClose}
          drawerType={AccountDetailsDrawerType.NewAccount}
          savingAccount={isSavingAccount}
        />
      )}

      <LoadingWithoutModal open={isLoading} />
    </>
  );
};

export default Accounts;
